import { ChangeEvent, Fragment, FunctionComponent } from 'react';
import {
    createTicketHolder,
    OrderTicketCollection,
    TicketHolder,
} from '../model/orderTicket.types';
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Stack,
    SvgIcon,
    TextField,
    Typography,
} from '@mui/material';
import last from 'lodash/last';
import { DatePicker, Select } from '@starjack/shared-components';
import { getYear } from 'date-fns';
import {
    Gender,
    MultiLangStringOption,
    StarjackDateRange,
} from '@starjack/utils';
import IconButton from '@mui/material/IconButton';
import { TrashIcon } from '@heroicons/react/24/solid';
import { getMoneyAsString } from '@starjack/entities/money';
import { useTranslation } from 'react-i18next';
import {
    OrderTicketErrorContext,
    OrderTicketFieldErrorMap,
} from '../lib/hooks/useOrderTicketValidation';
import { formatDateToString } from '../lib/orderTicketUtils';

type QuantityCounterProps = {
    initial?: number;
    value?: number;
    min?: number;
    onCounterChange: (next: number, action?: 'add' | 'subtract') => void;
};

type TicketHolderSelectionProps = {
    ticketCollection: OrderTicketCollection;
    onDelete: (ticketCollection: OrderTicketCollection) => void;
    onAddTicketHolder: (
        ticketCollection: OrderTicketCollection,
        ticketHolder: TicketHolder
    ) => void;
    onRemoveTicketHolder: (
        ticketCollection: OrderTicketCollection,
        ticketHolder: TicketHolder
    ) => void;
    onTicketHolderUpdate: (
        ticketCollection: OrderTicketCollection,
        ticketHolder: TicketHolder
    ) => void;
    validationErrors: OrderTicketFieldErrorMap | null;
    QuantityCounter: FunctionComponent<QuantityCounterProps>;
    showStartDay?: boolean;
};

export const TicketHolderSelection: FunctionComponent<
    TicketHolderSelectionProps
> = ({
    ticketCollection,
    onDelete,
    onAddTicketHolder,
    onRemoveTicketHolder,
    onTicketHolderUpdate,
    validationErrors,
    QuantityCounter,
    showStartDay = false,
}) => {
    const { t, i18n } = useTranslation();
    const { getBirtDayRangeString } = useBirthdayTranslation();

    const onNameChange =
        (
            field: 'firstname' | 'lastname' | 'gender',
            ticketHolder: TicketHolder
        ) =>
        (e: ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;

            const nextTicketHolder: TicketHolder = {
                ...ticketHolder,
                [field]: value,
            };

            onTicketHolderUpdate(ticketCollection, nextTicketHolder);
        };

    const onBirthdayChange = (ticketHolder: TicketHolder) => (date: Date) => {
        const nextTicketHolder: TicketHolder = {
            ...ticketHolder,
            birthDay: date,
        };

        onTicketHolderUpdate(ticketCollection, nextTicketHolder);
    };

    return (
        <Card
            sx={{
                minWidth: 0,
                width: '100%',
            }}
            raised
        >
            <CardContent
                sx={{
                    pt: { xs: 2.5, sm: 3 },
                    px: { xs: 2.5, sm: 3 },
                    pb: 0.5,
                    '&:last-child': { paddingBottom: 4 },
                }}
            >
                <Stack sx={{ '& > .MuiStack-root': { minWidth: 0 } }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 2,
                        }}
                    >
                        <Typography sx={{ flex: '1 1 auto', minWidth: 0 }}>
                            {
                                ticketCollection.name[
                                    i18n.language as MultiLangStringOption
                                ]
                            }
                            {showStartDay && (
                                <Typography
                                    component='span'
                                    variant='body2'
                                    sx={{ fontWeight: 500, ml: 0.5 }}
                                >
                                    ({t('ticketOrder.ticketHolder.validFrom')}:{' '}
                                    {formatDateToString(
                                        ticketCollection.startDay
                                    )}
                                    )
                                </Typography>
                            )}
                        </Typography>
                        <Button
                            variant='outlined'
                            size='small'
                            onClick={() => onDelete(ticketCollection)}
                        >
                            {t('ticketOrder.delete')}
                        </Button>
                    </Box>

                    <Stack spacing={1.5} divider={<Divider />} sx={{ mt: 3 }}>
                        {Object.values(ticketCollection.ticketMap).map(
                            (ticket) => {
                                const [person] = ticket.persons;
                                const birthdayRangeString =
                                    getBirtDayRangeString(person.birthday);
                                const ticketHolders =
                                    ticketCollection.ticketHoldersMap[
                                        ticket.productId
                                    ];

                                return (
                                    <Fragment key={ticket.productId}>
                                        <Typography
                                            variant='body2'
                                            color='text.secondary'
                                            sx={{ mb: 0.5 }}
                                        >
                                            {person.name.de}
                                            {birthdayRangeString &&
                                                ` (${birthdayRangeString})`}
                                        </Typography>
                                        <Stack
                                            direction='row'
                                            alignItems='center'
                                            spacing={2}
                                        >
                                            <QuantityCounter
                                                initial={0}
                                                value={ticketHolders.length}
                                                min={0}
                                                onCounterChange={(
                                                    _,
                                                    action
                                                ) => {
                                                    if (action === 'add') {
                                                        onAddTicketHolder(
                                                            ticketCollection,
                                                            createTicketHolder({
                                                                ticketId:
                                                                    ticket.productId,
                                                            })
                                                        );
                                                    } else {
                                                        const lastHolder =
                                                            last(ticketHolders);
                                                        if (lastHolder)
                                                            onRemoveTicketHolder(
                                                                ticketCollection,
                                                                lastHolder
                                                            );
                                                    }
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontWeight: 600,
                                                    ml: 'auto',
                                                }}
                                            >
                                                {getMoneyAsString(ticket.price)}
                                            </Typography>
                                        </Stack>

                                        {ticketHolders.length > 0 && (
                                            <Stack spacing={2} sx={{ mt: 1 }}>
                                                {ticketHolders.map(
                                                    (ticketHolder) => {
                                                        const errors =
                                                            validationErrors?.[
                                                                ticketHolder.id
                                                            ] || [];

                                                        return (
                                                            <Stack
                                                                key={
                                                                    ticketHolder.id
                                                                }
                                                                spacing={2}
                                                                direction={{
                                                                    xs: 'column',
                                                                    md: 'row',
                                                                }}
                                                                alignItems={{
                                                                    xs: 'stretch',
                                                                    md: 'flex-start',
                                                                }}
                                                                flexWrap='wrap'
                                                            >
                                                                <Select
                                                                    label={`${t(
                                                                        'ticketOrder.ticketHolder.gender.label'
                                                                    )} *`}
                                                                    sx={{
                                                                        width: {
                                                                            xs: '100%',
                                                                            md: 200,
                                                                        },
                                                                    }}
                                                                    onChange={onNameChange(
                                                                        'gender',
                                                                        ticketHolder
                                                                    )}
                                                                    {...getTextFieldErrorProps(
                                                                        'gender',
                                                                        errors
                                                                    )}
                                                                    value={
                                                                        ticketHolder.gender
                                                                    }
                                                                >
                                                                    <option value='--'>
                                                                        {t(
                                                                            'ticketOrder.ticketHolder.gender.selectTitle'
                                                                        )}
                                                                    </option>

                                                                    <option
                                                                        value={
                                                                            Gender.MALE
                                                                        }
                                                                    >
                                                                        {t(
                                                                            'ticketOrder.ticketHolder.gender.mr'
                                                                        )}
                                                                    </option>

                                                                    <option
                                                                        value={
                                                                            Gender.FEMALE
                                                                        }
                                                                    >
                                                                        {t(
                                                                            'ticketOrder.ticketHolder.gender.mrs'
                                                                        )}
                                                                    </option>
                                                                </Select>

                                                                <TextField
                                                                    sx={{
                                                                        width: {
                                                                            xs: '100%',
                                                                            md: 280,
                                                                        },
                                                                    }}
                                                                    defaultValue={
                                                                        ticketHolder.firstname ||
                                                                        undefined
                                                                    }
                                                                    onChange={onNameChange(
                                                                        'firstname',
                                                                        ticketHolder
                                                                    )}
                                                                    {...getTextFieldErrorProps(
                                                                        'firstname',
                                                                        errors
                                                                    )}
                                                                    label={`${t(
                                                                        'ticketOrder.ticketHolder.firstname'
                                                                    )} *`}
                                                                />

                                                                <TextField
                                                                    sx={{
                                                                        width: {
                                                                            xs: '100%',
                                                                            md: 280,
                                                                        },
                                                                    }}
                                                                    value={
                                                                        ticketHolder.lastname ||
                                                                        undefined
                                                                    }
                                                                    onChange={onNameChange(
                                                                        'lastname',
                                                                        ticketHolder
                                                                    )}
                                                                    {...getTextFieldErrorProps(
                                                                        'lastname',
                                                                        errors
                                                                    )}
                                                                    label={`${t(
                                                                        'ticketOrder.ticketHolder.lastname'
                                                                    )} *`}
                                                                />

                                                                <DatePicker
                                                                    label={`${t(
                                                                        'ticketOrder.ticketHolder.birthdate'
                                                                    )} *`}
                                                                    value={
                                                                        ticketHolder.birthDay
                                                                    }
                                                                    onChange={onBirthdayChange(
                                                                        ticketHolder
                                                                    )}
                                                                    textFieldProps={{
                                                                        ...getTextFieldErrorProps(
                                                                            'birthday',
                                                                            errors
                                                                        ),
                                                                        sx: {
                                                                            width: {
                                                                                xs: '100%',
                                                                                md: 280,
                                                                            },
                                                                        },
                                                                    }}
                                                                />

                                                                <IconButton
                                                                    sx={{
                                                                        alignSelf:
                                                                            'center',
                                                                    }}
                                                                    onClick={() => {
                                                                        onRemoveTicketHolder(
                                                                            ticketCollection,
                                                                            ticketHolder
                                                                        );
                                                                    }}
                                                                >
                                                                    <SvgIcon fontSize='medium'>
                                                                        <TrashIcon />
                                                                    </SvgIcon>
                                                                </IconButton>
                                                            </Stack>
                                                        );
                                                    }
                                                )}
                                            </Stack>
                                        )}
                                    </Fragment>
                                );
                            }
                        )}
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
};

const useBirthdayTranslation = () => {
    const { t } = useTranslation();

    const getBirtDayRangeString = (range: StarjackDateRange) => {
        if (!range) return null;

        const { from, to } = range;

        if (!from && !to) return null;
        if (from && !to)
            return `${t('ticketOrder.ticketHolder.from')} ${getYear(from)}`;
        if (from && to) return `${getYear(from)} - ${getYear(to)}`;
    };

    return {
        getBirtDayRangeString,
    };
};
const findError = (fieldName: string, errors: OrderTicketErrorContext[]) =>
    errors.find((error) => error.field === fieldName);

const getTextFieldErrorProps = (
    fieldName: string,
    errors: OrderTicketErrorContext[]
) => {
    const error = findError(fieldName, errors);
    return {
        error: !!error,
        helperText: error?.errorMessage,
    };
};
